/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../../Utils/RichEditor';
import { useSelector } from 'react-redux';

const STEP_SIX_FIELDS = [
   'general_terms_and_conditions',
   'project_termination_terms',
   'payment_terms',
   'application_ownership',
   'liability',
   'legal_jurisdiction',
];

// NEW: all Step 6 (Update) fields share the same 5000-character limit
const STEP6_MAX_LENGTH = 5000;

const StepSixUpdate = ({
   isSubmitting,
   values: valuesFromProps,
   setFieldValue: setFieldValueFromProps,
   touched,
   errors,
   initialValues,
}) => {
   const context = useFormikContext();
   const values = context?.values || valuesFromProps;
   const setFieldValue = context?.setFieldValue || setFieldValueFromProps;

   // Get contract update prefilled data from Redux
   const contractUpdatePrefilledData = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData,
   );
   const contractTypePreFillData = useSelector(
      (state) => state.contractState.contractTypePreFillData,
   );

   // Use ref to track if component has mounted and data has been set
   const componentMounted = useRef(false);
   const dataSetRef = useRef(false);

   // Track previous contract id so we can re-run prefill when switching contracts
   const prevContractIdRef = useRef(null);

   // NEW: live character-length errors for each field
   const [fieldLengthErrors, setFieldLengthErrors] = useState({});

   // NEW: checks length against STEP6_MAX_LENGTH and stores/clears the error message

   const getCharCount = (value) => {
      if (!value) return 0;
      return String(value).replace(/\s/g, '').length;   // strip whitespace only, keep HTML tags in the count
   };
   
   const checkLengthError = (fieldName, value) => {
      const length = getCharCount(value);
      setFieldLengthErrors((prev) => ({
         ...prev,
         [fieldName]:
            length > STEP6_MAX_LENGTH
               ? `This field is too long. Please keep it within ${STEP6_MAX_LENGTH} characters.`
               : '',
      }));
   };

   // Helper: split text into sentences (keeps punctuation) and return array of sentence strings
   const splitIntoSentences = (text) => {
      if (!text || typeof text !== 'string') return [];
      // Remove extra whitespace
      const normalized = text.replace(/\s+/g, ' ').trim();
      // Extract sentences (keeps punctuation). Fallback to whole text if no match.
      const matches = normalized.match(/[^.!?]+[.!?]*/g);
      return matches ? matches.map((s) => s.trim()) : [normalized];
   };

   // Helper: convert array to HTML unordered list.
   // - Each array element becomes exactly one <li> (preserve inner HTML if present).
   // - If an element already contains a list (<ul>/<ol>/<li>), keep it as-is.
   const arrayToHtmlList = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return '';
      const lis = arr.map((item) => {
         const str = item === null || item === undefined ? '' : String(item);
         // If the item already contains list markup, preserve it (append as-is)
         if (/<\/?(ul|ol|li)\b/i.test(str)) {
            return str;
         }
         // Preserve any inner HTML (paragraphs, bold, links, etc.) inside the li.
         return `<li>${str}</li>`;
      });
      return `<ul>${lis.join('')}</ul>`;
   };

   // Helper: normalize prefilled term content to HTML.
   // - If it's an array -> convert to <ul><li>...</li></ul> (sentence-split)
   // - If it's a string containing list tags -> return as-is
   // - If it's a plain string with multiple sentences -> convert to <ul><li>each sentence</li></ul>
   // - Otherwise wrap in a paragraph
   const toHtmlContent = (value) => {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return arrayToHtmlList(value);
      if (typeof value === 'string') {
         // if already contains list markup, return unchanged
         if (/<\/?(ul|ol|li)\b/i.test(value)) return value;
         // if contains other HTML tags return as-is
         if (/<[a-z][\s\S]*>/i.test(value)) {
            // Optionally, split inner text into sentences if you want bulletization
            // but avoid destroying existing formatting; return as-is.
            return value;
         }
         // plain text: split into sentences and convert to list if multiple sentences found
         const sentences = splitIntoSentences(value);
         if (sentences.length > 1) {
            const lis = sentences.map((s) => `<li>${s}</li>`).join('');
            return `<ul>${lis}</ul>`;
         }
         // single short line -> paragraph
         return `<p>${value}</p>`;
      }
      return String(value);
   };

   // Set data when component mounts or when contractUpdatePrefilledData is available
   useEffect(() => {
      componentMounted.current = true;

      const currentId = contractUpdatePrefilledData?.id || null;
      // If contract changed, allow re-setting fields
      if (prevContractIdRef.current !== currentId) {
         prevContractIdRef.current = currentId;
         dataSetRef.current = false;
      }

      const hasExistingUserData = STEP_SIX_FIELDS.some((field) => {
         const val = values?.[field];
         return typeof val === 'string' ? val.trim() !== '' : Boolean(val);
      });

      const hasContractTypeOverride = contractTypePreFillData !== null;

      if (hasContractTypeOverride) {
         dataSetRef.current = true;
         return () => {
            componentMounted.current = false;
         };
      }

      if (contractUpdatePrefilledData && !dataSetRef.current && !hasExistingUserData) {
         if (contractUpdatePrefilledData.general_term) {
            setFieldValue(
               'general_terms_and_conditions',
               toHtmlContent(contractUpdatePrefilledData.general_term),
            );
         }
         if (contractUpdatePrefilledData.termination_term) {
            setFieldValue(
               'project_termination_terms',
               toHtmlContent(contractUpdatePrefilledData.termination_term),
            );
         }
         if (contractUpdatePrefilledData.payment_term) {
            setFieldValue('payment_terms', toHtmlContent(contractUpdatePrefilledData.payment_term));
         }
         if (contractUpdatePrefilledData.app_ownership) {
            setFieldValue(
               'application_ownership',
               toHtmlContent(contractUpdatePrefilledData.app_ownership),
            );
         }
         if (contractUpdatePrefilledData.liability) {
            setFieldValue('liability', toHtmlContent(contractUpdatePrefilledData.liability));
         }

         if (contractUpdatePrefilledData.legal_jurisdiction) {
            setFieldValue(
               'legal_jurisdiction',
               toHtmlContent(contractUpdatePrefilledData.legal_jurisdiction),
            );
         }

         dataSetRef.current = true;
      }

      return () => {
         componentMounted.current = false;
      };
   }, [contractUpdatePrefilledData?.id, contractTypePreFillData, setFieldValue, values]);

   // Get current value for each field, prioritizing user input over prefilled data
   // CRITICAL: Always returns a string (never undefined/null) to prevent CKEditor crashes
   const getCurrentValue = (fieldName) => {
      const value = values && values[fieldName];

      // Explicitly handle undefined/null by returning empty string
      if (value === undefined || value === null) return '';
      // If value is not a string, convert it
      return typeof value === 'string' ? value : String(value || '');
   };

   // Safe wrapper for CKEditor data prop to prevent crashes from undefined/null values
   const renderCKEditor = (fieldName) => {
      const dataValue = getCurrentValue(fieldName);

      // EXTRA GUARD: CKEditor's React component crashes if 'data' is undefined/null during mount.
      // Also some types of objects can confuse it if they aren't plain strings or specific data structures.
      const safeData = typeof dataValue === 'string' ? dataValue : '';

      return (
         <CKEditor
            key={`ckeditor-${fieldName}`}
            editor={ClassicEditor}
            data={safeData}
            onBlur={(event, editor) => {
               const data = editor.getData();
               if (values[fieldName] !== data) {
                  setFieldValue(fieldName, data);
               }
               checkLengthError(fieldName, data); {/* NEW */ }
            }}
            onReady={(editor) => {
               const fieldData = values[fieldName];
               const safeData = toHtmlContent(fieldData);
               editor.setData(safeData);
            }}
         />
      );
   };

   return (
      <>
         <div className="card stepCard">
            <Box className="card-body" sx={{ padding: '15px' }}>
               <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                     <Box className="commonBox">
                        <Grid container spacing={2}>
                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 General Terms & Conditions
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="general_terms_and_conditions">
                                       {({ field }) => (<>
                                          {renderCKEditor('general_terms_and_conditions')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {getCharCount(values.general_terms_and_conditions)} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.general_terms_and_conditions && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.general_terms_and_conditions}
                                             </Typography>
                                          )}
                                       </>
                                       )
                                       }
                                    </Field>
                                    {touched["general_terms_and_conditions"] && errors["general_terms_and_conditions"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["general_terms_and_conditions"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="general_terms_and_conditions"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>

                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Project Termination Terms
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="project_termination_terms">
                                       {({ field }) => (
                                          <>
                                             {renderCKEditor('project_termination_terms')}
                                             {field.value && (
                                                <Typography
                                                   variant="caption"
                                                   color="GrayText"
                                                   sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                   {getCharCount(values.project_termination_terms)} characters
                                                </Typography>
                                             )}
                                             {/* NEW: red length-limit error, shown like a helper text below the field */}
                                             {fieldLengthErrors.project_termination_terms && (
                                                <Typography
                                                   variant="caption"
                                                   sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                                >
                                                   {fieldLengthErrors.project_termination_terms}
                                                </Typography>
                                             )}
                                          </>
                                       )}
                                    </Field>
                                    {touched["project_termination_terms"] && errors["project_termination_terms"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["project_termination_terms"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="project_termination_terms"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>

                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Payment Terms & Conditions
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="payment_terms">
                                       {({ field }) => (<>
                                          {renderCKEditor('payment_terms')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {getCharCount(values.payment_terms)} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.payment_terms && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.payment_terms}
                                             </Typography>
                                          )}
                                       </>)
                                       }
                                    </Field>
                                    {touched["payment_terms"] && errors["payment_terms"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["payment_terms"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="payment_terms"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>

                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Application Ownership
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="application_ownership">
                                       {({ field }) => (<>
                                          {renderCKEditor('application_ownership')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {getCharCount(values.application_ownership)} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.application_ownership && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.application_ownership}
                                             </Typography>
                                          )}
                                       </>
                                       )}
                                    </Field>
                                    {touched["application_ownership"] && errors["application_ownership"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["application_ownership"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="application_ownership"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>

                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Liability
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="liability">
                                       {({ field }) => (
                                          <>
                                             {renderCKEditor('liability')}
                                             {field.value && (
                                                <Typography
                                                   variant="caption"
                                                   color="GrayText"
                                                   sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                   {getCharCount(values.liability)} characters
                                                </Typography>
                                             )}
                                             {/* NEW: red length-limit error, shown like a helper text below the field */}
                                             {fieldLengthErrors.liability && (
                                                <Typography
                                                   variant="caption"
                                                   sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                                >
                                                   {fieldLengthErrors.liability}
                                                </Typography>
                                             )}
                                          </>
                                       )}
                                    </Field>
                                    {touched["liability"] && errors["liability"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["liability"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="liability"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>

                           <Grid item md={12} xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Legal Jurisdiction
                              </Typography>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <Field name="legal_jurisdiction">
                                       {({ field }) => (
                                          <>
                                             {renderCKEditor('legal_jurisdiction')}
                                             {field.value && (
                                                <Typography
                                                   variant="caption"
                                                   color="GrayText"
                                                   sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                   {getCharCount(values.legal_jurisdiction)} characters
                                                </Typography>
                                             )}
                                             {/* NEW: red length-limit error, shown like a helper text below the field */}
                                             {fieldLengthErrors.legal_jurisdiction && (
                                                <Typography
                                                   variant="caption"
                                                   sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                                >
                                                   {fieldLengthErrors.legal_jurisdiction}
                                                </Typography>
                                             )}
                                          </>
                                       )}
                                    </Field>
                                    {touched["legal_jurisdiction"] && errors["legal_jurisdiction"] && (
                                       <Typography
                                          variant="caption"
                                          sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                       >
                                          {errors["legal_jurisdiction"]}
                                       </Typography>
                                    )}
                                    {/* <ErrorMessage
                                       name="legal_jurisdiction"
                                       component="div"
                                       style={{ color: 'red' }}
                                    /> */}
                                 </Grid>
                              </Grid>
                           </Grid>
                        </Grid>
                     </Box>
                  </Grid>
               </Grid>
            </Box>
         </div>
      </>
   );
};

export default StepSixUpdate;